import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, type Lang } from "@/components/luminous/primitives";
import { ServiceDiagram, type DiagramVariant } from "@/components/service-page/ServiceDiagram";
import { technicalSeoPage } from "@/data/money-pages/technicalSeo";
import { googleAdsPage } from "@/data/money-pages/googleAds";
import { seoConsultantPage } from "@/data/money-pages/seoConsultant";
import { seoAuditPage } from "@/data/money-pages/seoAudit";
import { internationalSeoPage } from "@/data/money-pages/internationalSeo";
import { hispanicSeoPage } from "@/data/money-pages/hispanicSeo";
import { ecommerceSeoPage } from "@/data/money-pages/ecommerceSeo";
import { amazonSeoPage } from "@/data/money-pages/amazonSeo";
import { aiSearchPage } from "@/data/money-pages/aiSearch";
import { seoSemStrategiesPage } from "@/data/money-pages/seoSemStrategies";
import { rebrandingSeoPage } from "@/data/money-pages/rebrandingSeo";
import { contentConversionPage } from "@/data/money-pages/contentConversion";
import { brandingPage } from "@/data/money-pages/branding";
import { websiteRedesignPage } from "@/data/money-pages/websiteRedesign";
import type { MoneyPageContent } from "@/data/money-pages/types";
import { enRoutes, esRoutes } from "@/utils/routes";

/**
 * Índice de servicios: tres servicios prioritarios en composiciones abiertas
 * (problema, alcance y acceso diferenciados) y un índice secundario para los
 * servicios complementarios. Los textos provienen del contenido ya aprobado
 * de cada página de servicio: no se inventa copy comercial nuevo.
 */

const copy = {
  es: {
    priorityLabel: "Servicios principales",
    priorityTitle: "Tres puntos de entrada, tres problemas distintos",
    priorityIntro:
      "No son paquetes: son tres formas de resolver un cuello de botella distinto. El punto de entrada depende de si el problema está en el acceso técnico, en la visibilidad del área que atiendes o en la demanda que necesitas comprar ahora.",
    problem: "Problema que resuelve",
    scope: "Alcance",
    access: "Cómo se accede",
    open: "Ver el servicio",
    secondaryLabel: "Servicios complementarios",
    secondaryTitle: "Capacidades que se suman cuando el proyecto lo requiere",
    secondaryIntro:
      "Se contratan dentro de un proyecto existente o como trabajo específico, siempre después de entender el problema principal.",
    local: {
      name: "SEO local",
      problem:
        "La empresa presta servicio en municipios concretos, pero no aparece cuando alguien busca desde esa zona ni en el mapa.",
      scope: [
        "Coherencia de nombre, dirección y teléfono en las fuentes que consultan los buscadores",
        "Ficha de empresa y contenido por área de servicio realmente atendida",
        "Páginas locales con información propia, no plantillas repetidas",
        "Seguimiento de llamadas, rutas y formularios de origen local",
      ],
      access:
        "Se revisa la sede real y el área de servicio antes de proponer alcance. No se crean oficinas ni direcciones que no existen.",
    },
    diagnosis: "Solicitar diagnóstico",
  },
  en: {
    priorityLabel: "Core services",
    priorityTitle: "Three entry points, three different problems",
    priorityIntro:
      "These are not packages: they solve three different bottlenecks. The entry point depends on whether the problem is technical access, visibility in the area you serve, or the demand you need to buy right now.",
    problem: "Problem it solves",
    scope: "Scope",
    access: "How it starts",
    open: "See the service",
    secondaryLabel: "Complementary services",
    secondaryTitle: "Capabilities added when the project calls for them",
    secondaryIntro:
      "They are engaged inside an existing project or as specific work, always after the main problem is understood.",
    local: {
      name: "Local SEO",
      problem:
        "The company serves specific areas but does not appear when someone searches from there, nor on the map.",
      scope: [
        "Consistent name, address and phone across the sources search engines read",
        "Business listing and content per service area actually covered",
        "Local pages with their own information, not repeated templates",
        "Tracking of calls, directions and forms with local origin",
      ],
      access:
        "The real office and the service area are reviewed before proposing scope. No offices or addresses are invented.",
    },
    diagnosis: "Request a diagnosis",
  },
} as const;

type Priority = {
  name: string;
  problem: string;
  scope: string[];
  access: string;
  to: string;
  diagram: DiagramVariant;
};

const fromMoneyPage = (
  page: MoneyPageContent,
  language: Lang,
  diagram: DiagramVariant,
): Priority => {
  const t = page[language];
  return {
    name: t.breadcrumbLabel,
    problem: t.heroDescription,
    scope: t.scope.includes.items.slice(0, 4),
    access: t.summary[0]?.text ?? t.heroSubtitle,
    to: language === "es" ? page.esPath : page.enPath,
    diagram,
  };
};

const secondaryPages: MoneyPageContent[] = [
  seoConsultantPage,
  seoAuditPage,
  aiSearchPage,
  internationalSeoPage,
  hispanicSeoPage,
  ecommerceSeoPage,
  amazonSeoPage,
  seoSemStrategiesPage,
  contentConversionPage,
  brandingPage,
  websiteRedesignPage,
  rebrandingSeoPage,
];

export const ServicesHubComposition = ({ language }: { language: Lang }) => {
  const c = copy[language];
  const routes = language === "en" ? enRoutes : esRoutes;

  const priorities: Priority[] = [
    fromMoneyPage(technicalSeoPage, language, "architecture"),
    {
      name: c.local.name,
      problem: c.local.problem,
      scope: [...c.local.scope],
      access: c.local.access,
      to: routes.localSeoHub,
      diagram: "local",
    },
    fromMoneyPage(googleAdsPage, language, "intent"),
  ];

  return (
    <>
      <section className="v3-section-tight bg-[color:var(--v3-mineral)]">
        <div className="v3-shell">
          <header className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-14">
            <div className="lg:col-span-7">
              <p className="v3-micro">{c.priorityLabel}</p>
              <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.3rem)" }}>
                {c.priorityTitle}
              </h2>
            </div>
            <p className="v3-body lg:col-span-5">{c.priorityIntro}</p>
          </header>

          <div className="mt-12 space-y-12 lg:space-y-20">
            {priorities.map((service, i) => (
              <Reveal as="article" key={service.to}>
                <div className="border-t border-[color:var(--v3-line)] pt-8">
                  <div className="grid gap-9 lg:grid-cols-12 lg:gap-14">
                    <div
                      className={`min-w-0 lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}
                    >
                      <p className="v3-micro" style={{ color: "var(--v3-blue)" }}>
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3
                        className="v3-display mt-3"
                        style={{ fontSize: "clamp(1.35rem, 2vw, 1.75rem)" }}
                      >
                        {service.name}
                      </h3>
                      <p className="v3-micro mt-6">{c.problem}</p>
                      <p className="v3-body mt-2">{service.problem}</p>

                      <p className="v3-micro mt-7">{c.scope}</p>
                      <ul className="mt-3 space-y-2">
                        {service.scope.map((item) => (
                          <li
                            key={item}
                            className="flex gap-2.5 text-[0.93rem] leading-snug text-[color:var(--v3-ink-soft)]"
                          >
                            <Check
                              className="mt-0.5 h-4 w-4 shrink-0"
                              style={{ color: "var(--v3-blue)" }}
                              aria-hidden
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <p className="v3-micro mt-7">{c.access}</p>
                      <p className="v3-note mt-2">{service.access}</p>

                      <Link to={service.to} className="v3-btn mt-7">
                        {c.open}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </div>
                    <div
                      className={`min-w-0 lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1 lg:row-start-1" : ""}`}
                    >
                      <ServiceDiagram variant={service.diagram} language={language} />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="v3-section-tight bg-[color:var(--v3-mineral-deep)]">
        <div className="v3-shell">
          <header className="max-w-[54rem]">
            <p className="v3-micro">{c.secondaryLabel}</p>
            <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.4rem, 2.1vw, 1.95rem)" }}>
              {c.secondaryTitle}
            </h2>
            <p className="v3-body mt-4">{c.secondaryIntro}</p>
          </header>

          <ul className="mt-10 grid gap-x-14 md:grid-cols-2">
            {secondaryPages.map((page) => {
              const t = page[language];
              const to = language === "es" ? page.esPath : page.enPath;
              return (
                <li key={page.id} className="border-t border-[color:var(--v3-line)]">
                  <Link
                    to={to}
                    className="group flex gap-4 py-5 transition-colors"
                    style={{ transitionDuration: "var(--v3-t-control)" }}
                  >
                    <span className="flex-1">
                      <span className="block text-[0.98rem] font-semibold text-[color:var(--v3-ink)]">
                        {t.breadcrumbLabel}
                      </span>
                      <span className="v3-note mt-1.5 block">{t.heroSubtitle}</span>
                    </span>
                    <ArrowRight
                      className="mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1 group-focus-visible:translate-x-1"
                      style={{ color: "var(--v3-blue)", transitionDuration: "var(--v3-t-control)" }}
                      aria-hidden
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </>
  );
};
